import React, { Component } from "react";
import Layout from "../../../Layout/layout";
import Helmet from "react-helmet";
import axios from "axios";
import { saveAs } from "file-saver";
import GridLoader from "react-spinners/GridLoader";
import { apiHost } from "../../../api/apiClient";
import "../../../styles/reports/checklistRunsReport.scss";

const FALLBACK_TZ = "Australia/Melbourne";

const getValue = (obj, key) => {
  if (!obj) return null;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  return obj[pascal];
};

const hasText = (value) => String(value || "").trim() !== "";

const normalizeTz = (tz) => {
  const value = String(tz || "").trim();
  if (!value) return FALLBACK_TZ;
  try {
    new Intl.DateTimeFormat("en-AU", { timeZone: value }).format(new Date());
    return value;
  } catch (e) {
    return FALLBACK_TZ;
  }
};

const parseDate = (value) => {
  if (!value) return null;
  const text = String(value).trim();
  const mysqlMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (mysqlMatch) {
    return new Date(Date.UTC(
      Number(mysqlMatch[1]),
      Number(mysqlMatch[2]) - 1,
      Number(mysqlMatch[3]),
      Number(mysqlMatch[4]),
      Number(mysqlMatch[5]),
      Number(mysqlMatch[6])
    ));
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDbDate = (value, tz) => {
  const date = parseDate(value);
  if (!date) return value || "-";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      timeZone: normalizeTz(tz),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(date);
  } catch (e) {
    return value || "-";
  }
};

const durationText = (start, end) => {
  const started = parseDate(start);
  const completed = parseDate(end);
  if (!started || !completed) return "-";
  const diff = Math.max(0, Math.floor((completed.getTime() - started.getTime()) / 1000));
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;
  return hours > 0 ? `${hours}h ${minutes}m ${seconds}s` : `${minutes}m ${seconds}s`;
};

const statusClass = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "pass") return "pass";
  if (value === "fail") return "fail";
  if (value === "logged") return "logged";
  if (value === "system") return "system";
  return "muted";
};

const displayStatus = (row) => {
  const source = String(getValue(row, "source") || "").trim().toLowerCase();
  return source === "system" ? "system" : String(getValue(row, "status") || "").trim().toLowerCase();
};

const checklistLabel = (value) => {
  if (value === "pre_start") return "PRE-START";
  if (value === "post_trip") return "POST-TRIP";
  return value || "-";
};

const sourceLabel = (value) => {
  const source = String(value || "driver").toLowerCase();
  return source === "system" ? "SYSTEM" : "DRIVER";
};

const SPLIT_STORAGE_KEY = "driver_logs_checklist_split_pct";

class ChecklistRunsReport extends Component {
  constructor(props) {
    super(props);
    const savedSplit = Number(localStorage.getItem(SPLIT_STORAGE_KEY));
    this.state = {
      rows: [],
      selectedRun: null,
      selectedItems: [],
      selectedKey: "",
      isLoading: false,
      detailsLoading: false,
      isExporting: false,
      errorMessage: "",
      date: "",
      page: 1,
      pageSize: 100,
      hasMore: false,
      filters: {
        type: "all",
        status: "all",
        notes: "all",
        search: "",
      },
      splitPct: Number.isFinite(savedSplit) && savedSplit >= 40 && savedSplit <= 76 ? savedSplit : 62,
      resizing: false,
    };
    this.gridRef = React.createRef();
    this.tableWrapRef = React.createRef();
  }

  componentDidMount() {
    this.loadRuns();
    document.addEventListener("mousemove", this.handleSplitterMove);
    document.addEventListener("mouseup", this.stopResize);
    document.addEventListener("mouseleave", this.stopResize);
  }

  componentWillUnmount() {
    document.removeEventListener("mousemove", this.handleSplitterMove);
    document.removeEventListener("mouseup", this.stopResize);
    document.removeEventListener("mouseleave", this.stopResize);
  }

  loadRuns = async (requestedPage = 1) => {
    const page = Number.isFinite(requestedPage) ? Math.max(1, requestedPage) : 1;
    this.setState({ isLoading: true, errorMessage: "", selectedRun: null, selectedItems: [], selectedKey: "" });
    try {
      const params = { page, pageSize: this.state.pageSize };
      if (this.state.date) {
        params.date = this.state.date;
        params.timezone = localStorage.getItem("timeZoneID") || FALLBACK_TZ;
      }
      const response = await axios.get(`${apiHost}/api/Report/ChecklistRuns`, {
        params,
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });

      const rows = (response.data && response.data.rows ? response.data.rows : []).map((row) => ({
        ...row,
        rowKey: getValue(row, "rowKey") || `${getValue(row, "recordKind")}-${getValue(row, "id")}`,
      }));
      this.setState({
        rows,
        page: Number(response.data && response.data.page) || page,
        hasMore: Boolean(response.data && response.data.hasMore),
        isLoading: false,
      }, () => {
        if (this.tableWrapRef.current) this.tableWrapRef.current.scrollTop = 0;
      });
    } catch (e) {
      const message = e && e.response && e.response.data && e.response.data.error
        ? e.response.data.error
        : String(e);
      this.setState({ isLoading: false, errorMessage: message });
    }
  };

  loadDetails = async (run) => {
    const recordKind = getValue(run, "recordKind");
    const rowKey = getValue(run, "rowKey");
    this.setState({ selectedRun: run, selectedItems: [], selectedKey: rowKey || "", detailsLoading: recordKind === "checklist" });

    if (recordKind !== "checklist") {
      return;
    }

    try {
      const response = await axios.get(`${apiHost}/api/Report/ChecklistRuns/${getValue(run, "id")}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      this.setState({
        selectedRun: response.data.run || run,
        selectedItems: response.data.items || [],
        detailsLoading: false,
      });
    } catch (e) {
      this.setState({ detailsLoading: false, errorMessage: "Checklist details could not be loaded." });
    }
  };

  setFilter = (name, value) => {
    this.setState((state) => ({
      filters: {
        ...state.filters,
        [name]: value,
      },
    }));
  };

  clearDate = () => {
    this.setState({ date: "" }, () => this.loadRuns(1));
  };

  filterRows = (rows) => {
    const { filters } = this.state;
    const search = filters.search.trim().toLowerCase();
    return rows.filter((row) => {
      const type = getValue(row, "checklistType");
      const status = displayStatus(row);
      const notesCount = Number(getValue(row, "notesCount") || 0);

      if (filters.type !== "all" && type !== filters.type) return false;
      if (filters.status !== "all" && status !== filters.status) return false;
      if (filters.notes === "with_notes" && notesCount <= 0) return false;

      if (!search) return true;
      const haystack = [
        getValue(row, "id"),
        getValue(row, "driverName"),
        getValue(row, "assetName"),
        getValue(row, "assetFriendly"),
        getValue(row, "customerName"),
        getValue(row, "depotName"),
        getValue(row, "source"),
        getValue(row, "reason"),
      ].join(" ").toLowerCase();
      return haystack.indexOf(search) !== -1;
    });
  };

  getFilteredRows = () => this.filterRows(this.state.rows);

  exportExcel = async () => {
    this.setState({ isExporting: true, errorMessage: "" });
    try {
      const allRows = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const params = { page, pageSize: 500 };
        if (this.state.date) {
          params.date = this.state.date;
          params.timezone = localStorage.getItem("timeZoneID") || FALLBACK_TZ;
        }
        const response = await axios.get(`${apiHost}/api/Report/ChecklistRuns`, {
          params,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        allRows.push(...(response.data && response.data.rows ? response.data.rows : []));
        hasMore = Boolean(response.data && response.data.hasMore);
        page += 1;
      }

      const rows = this.filterRows(allRows);
      const headers = [
        "ID", "Record Type", "Completed", "Driver", "Driver ID", "Asset", "Asset ID",
        "Customer", "Depot", "Checklist/Event", "Status", "Source", "Reason", "Event Mode",
        "Odometer", "Items", "Notes", "Checklist Run ID",
      ];
      const csvValue = (value) => {
        let text = String(value == null ? "" : value);
        if (/^[=+\-@]/.test(text)) text = `'${text}`;
        return `"${text.replace(/"/g, '""')}"`;
      };
      const lines = [headers.map(csvValue).join(",")];
      rows.forEach((row) => {
        const tz = getValue(row, "depotTimezone");
        lines.push([
          getValue(row, "displayId") || getValue(row, "id"),
          getValue(row, "recordKind"),
          formatDbDate(getValue(row, "completedAt"), tz),
          getValue(row, "driverName"),
          getValue(row, "driverId"),
          this.assetDisplay(row),
          getValue(row, "assetId"),
          getValue(row, "customerName"),
          getValue(row, "depotName"),
          checklistLabel(getValue(row, "checklistType")),
          displayStatus(row).toUpperCase(),
          hasText(getValue(row, "source")) ? sourceLabel(getValue(row, "source")) : "",
          getValue(row, "reason"),
          getValue(row, "eventMode"),
          getValue(row, "odometer"),
          getValue(row, "responseCount") || 0,
          getValue(row, "notesCount") || 0,
          getValue(row, "checklistRunId"),
        ].map(csvValue).join(","));
      });

      const fileDate = this.state.date || new Date().toISOString().slice(0, 10);
      saveAs(new Blob(["\ufeff", lines.join("\r\n")], { type: "text/csv;charset=utf-8" }), `driver-logs-${fileDate}.csv`);
      this.setState({ isExporting: false });
    } catch (e) {
      this.setState({ isExporting: false, errorMessage: "Driver Logs Excel export could not be created." });
    }
  };

  exportPdf = () => {
    const run = this.state.selectedRun;
    if (!run || getValue(run, "recordKind") !== "checklist") return;

    const tz = getValue(run, "depotTimezone");
    const items = this.state.selectedItems;
    const html = `
      <html>
      <head>
        <title>Checklist Run ${getValue(run, "id")}</title>
        <style>
          body { font-family: Arial, sans-serif; color: #1f2937; margin: 24px; }
          h1 { margin: 0 0 12px; color: #194991; }
          .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 18px; margin-bottom: 18px; font-size: 13px; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #d8e1ec; padding: 8px; text-align: left; font-size: 12px; vertical-align: top; }
          th { background: #eef4ff; color: #194991; }
          .critical td { background: #fff6f6; }
        </style>
      </head>
      <body>
        <h1>Driver Logs</h1>
        <div class="meta">
          <div><strong>Run:</strong> ${getValue(run, "id")}</div>
          <div><strong>Status:</strong> ${getValue(run, "status") || "-"}</div>
          <div><strong>Customer:</strong> ${getValue(run, "customerName") || "-"}</div>
          <div><strong>Depot:</strong> ${getValue(run, "depotName") || "-"}</div>
          <div><strong>Driver:</strong> ${getValue(run, "driverName") || "-"}</div>
          <div><strong>Asset:</strong> ${this.assetDisplay(run)}</div>
          <div><strong>Checklist:</strong> ${checklistLabel(getValue(run, "checklistType"))} ${getValue(run, "templateName") || ""}</div>
          <div><strong>Completed:</strong> ${formatDbDate(getValue(run, "completedAt"), tz)}</div>
          <div><strong>Duration:</strong> ${durationText(getValue(run, "startedAt"), getValue(run, "completedAt"))}</div>
          <div><strong>Odometer:</strong> ${getValue(run, "odometer") || "-"}</div>
        </div>
        <table>
          <thead><tr><th>#</th><th>Checklist Item</th><th>Response</th><th>Notes</th></tr></thead>
          <tbody>
            ${items.map((item, index) => `
              <tr class="${getValue(item, "critical") ? "critical" : ""}">
                <td>${index + 1}</td>
                <td>${this.escapeHtml(getValue(item, "itemLabel") || "-")}</td>
                <td>${this.escapeHtml(getValue(item, "response") || "-")}</td>
                <td>${this.escapeHtml(getValue(item, "notes") || "-")}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </body>
      </html>`;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  escapeHtml = (value) => String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  assetDisplay = (row) => {
    const assetName = getValue(row, "assetName") || getValue(row, "assetId") || "-";
    const friendly = getValue(row, "assetFriendly");
    return hasText(friendly) ? `${assetName} (${friendly})` : assetName;
  };

  renderBadge = (className, label) => (
    <span className={`checklist-badge ${className}`}>{label}</span>
  );

  renderRows = () => {
    const rows = this.getFilteredRows();
    if (!rows.length) {
      return (
        <tr>
          <td colSpan="9" className="checklist-empty">No records match the selected filters.</td>
        </tr>
      );
    }

    return rows.map((row) => {
      const tz = getValue(row, "depotTimezone");
      const rowKey = getValue(row, "rowKey");
      const type = getValue(row, "checklistType");
      const status = displayStatus(row);
      return (
        <tr
          key={rowKey}
          className={this.state.selectedKey === rowKey ? "active" : ""}
          onClick={() => this.loadDetails(row)}
        >
          <td>{getValue(row, "displayId") || getValue(row, "id")}</td>
          <td>{formatDbDate(getValue(row, "completedAt"), tz)}</td>
          <td>{getValue(row, "driverName") || "-"}</td>
          <td title={getValue(row, "assetId") || ""}>{this.assetDisplay(row)}</td>
          <td>{getValue(row, "depotName") || "-"}</td>
          <td>{this.renderBadge(type === "post_trip" ? "post" : type === "pre_start" ? "pre" : "muted", checklistLabel(type))}</td>
          <td>{this.renderBadge(statusClass(status), String(status || "-").toUpperCase())}</td>
          <td>{getValue(row, "responseCount") || 0}</td>
          <td>{getValue(row, "notesCount") || 0}</td>
        </tr>
      );
    });
  };

  renderDetail = () => {
    const run = this.state.selectedRun;
    if (!run) {
      return <div className="checklist-empty detail-empty">Select a run to view checklist responses.</div>;
    }

    const tz = getValue(run, "depotTimezone");
    const isEvent = getValue(run, "recordKind") !== "checklist";
    return (
      <>
        <div className={`checklist-detail-meta ${displayStatus(run) === "fail" ? "fail" : ""}`}>
          <div><strong>Customer:</strong> {getValue(run, "customerName") || "-"}</div>
          <div><strong>Depot:</strong> {getValue(run, "depotName") || "-"}</div>
          <div><strong>Driver:</strong> {getValue(run, "driverName") || "-"}</div>
          <div><strong>Asset:</strong> {this.assetDisplay(run)}</div>
          <div><strong>Completed:</strong> {formatDbDate(getValue(run, "completedAt"), tz)}</div>
          <div><strong>{isEvent ? "Event" : "Checklist"}:</strong> {checklistLabel(getValue(run, "checklistType"))} {getValue(run, "templateName") || ""}</div>
          <div><strong>Status:</strong> {displayStatus(run).toUpperCase() || "-"}</div>
          <div><strong>Odometer:</strong> {getValue(run, "odometer") || "-"}</div>
          {isEvent && <div><strong>Source:</strong> {sourceLabel(getValue(run, "source"))}</div>}
          {isEvent && hasText(getValue(run, "reason")) && <div><strong>Reason:</strong> {getValue(run, "reason")}</div>}
          {!isEvent && <div><strong>Duration:</strong> {durationText(getValue(run, "startedAt"), getValue(run, "completedAt"))}</div>}
        </div>
        {isEvent ? this.renderDriverEventDetail(run) : this.renderChecklistItems()}
        {!isEvent && this.renderDeclaration(run, tz)}
      </>
    );
  };

  renderDriverEventDetail = (run) => {
    const reason = getValue(run, "reason");
    return (
      <div className="checklist-empty detail-empty">
        {hasText(reason) ? reason : "Driver event. No checklist questions were asked."}
      </div>
    );
  };

  renderChecklistItems = () => {
    if (this.state.detailsLoading) {
      return (
        <div className="checklist-detail-loading">
          <GridLoader size={7} color={"#222222"} loading />
          <span>Loading checklist responses</span>
        </div>
      );
    }

    if (!this.state.selectedItems.length) {
      return <div className="checklist-empty detail-empty">No checklist responses were stored for this run.</div>;
    }

    return (
      <div className="checklist-items-wrap">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Checklist Item</th>
              <th>Response</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {this.state.selectedItems.map((item, index) => (
              <tr key={`${getValue(item, "itemId")}-${index}`} className={getValue(item, "critical") ? "critical" : ""}>
                <td>{index + 1}</td>
                <td>
                  {getValue(item, "itemLabel") || "-"}
                  {getValue(item, "critical") && this.renderBadge("critical", "CRITICAL")}
                </td>
                <td>{this.renderBadge(statusClass(getValue(item, "response")), String(getValue(item, "response") || "-").toUpperCase())}</td>
                <td>{hasText(getValue(item, "notes")) ? <span className="notes-text">{getValue(item, "notes")}</span> : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  renderDeclaration = (run, tz) => {
    const declared = getValue(run, "driverDeclared") === true || Number(getValue(run, "driverDeclared") || 0) === 1;
    const declaredAt = getValue(run, "driverDeclaredAt");
    return (
      <div className="checklist-declaration">
        <strong>Declaration:</strong>{" "}
        {declared ? (
          <>
            Driver declaration acknowledged by {getValue(run, "driverName") || "-"}
            {declaredAt ? ` at ${formatDbDate(declaredAt, tz)}` : ""}.
          </>
        ) : (
          "No driver declaration flag is stored for this run."
        )}
      </div>
    );
  };

  renderFilterButton = (group, value, label) => (
    <button
      type="button"
      className={this.state.filters[group] === value ? "active" : ""}
      onClick={() => this.setFilter(group, value)}
    >
      {label}
    </button>
  );

  startResize = (event) => {
    event.preventDefault();
    this.setState({ resizing: true });
  };

  stopResize = () => {
    if (this.state.resizing) {
      this.setState({ resizing: false });
    }
  };

  handleSplitterMove = (event) => {
    if (!this.state.resizing || !this.gridRef.current) {
      return;
    }

    const rect = this.gridRef.current.getBoundingClientRect();
    if (!rect.width) {
      return;
    }

    const pct = ((event.clientX - rect.left) / rect.width) * 100;
    const clamped = Math.max(40, Math.min(76, pct));
    localStorage.setItem(SPLIT_STORAGE_KEY, String(clamped));
    this.setState({ splitPct: clamped });
  };

  render() {
    const rows = this.getFilteredRows();
    const selected = this.state.selectedRun;
    const canExport = selected && getValue(selected, "recordKind") === "checklist";

    return (
      <Layout>
        <Helmet>
          <title>Driver Logs</title>
        </Helmet>
        <div className="contentWrapper contentWrapperSmoothRelease report-modern-page checklist-runs-page">
          <div className="reportHeaderTile">
            <div className="headingContainer report-modern-header">
              <div>
                <h2 className="mainHeading">Driver Logs</h2>
                <p>Checklist runs and odometer-only driver logs for assets you can access.</p>
              </div>
            </div>

            <div className="checklist-toolbar inputFormGroup reportPickerGrid reportPickerToolbar">
              <div className="checklist-filter-group">
                <span>Checklist</span>
                {this.renderFilterButton("type", "all", "All")}
                {this.renderFilterButton("type", "pre_start", "Pre-Start")}
                {this.renderFilterButton("type", "post_trip", "Post-Trip")}
              </div>
              <div className="checklist-filter-group">
                <span>Status</span>
                {this.renderFilterButton("status", "all", "All")}
                {this.renderFilterButton("status", "pass", "PASS")}
                {this.renderFilterButton("status", "fail", "FAIL")}
                {this.renderFilterButton("status", "logged", "LOGGED")}
                {this.renderFilterButton("status", "system", "SYSTEM")}
              </div>
              <div className="checklist-filter-group">
                <span>Notes</span>
                {this.renderFilterButton("notes", "all", "All")}
                {this.renderFilterButton("notes", "with_notes", "With Notes")}
              </div>
              <div className="checklist-filter-group">
                <span>Date</span>
                <input
                  type="date"
                  value={this.state.date}
                  onChange={(event) => this.setState({ date: event.target.value })}
                />
                <button type="button" onClick={() => this.loadRuns(1)}>Run Search</button>
                <button type="button" onClick={this.clearDate}>Latest 100</button>
              </div>
              <div className="checklist-filter-group search">
                <span>Search</span>
                <input
                  type="search"
                  placeholder="Run ID, driver, asset, customer, depot"
                  value={this.state.filters.search}
                  onChange={(event) => this.setFilter("search", event.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="reportBody">
            {this.state.errorMessage && <div className="checklist-error">{this.state.errorMessage}</div>}

            <div
              ref={this.gridRef}
              className={this.state.resizing ? "checklist-report-grid resizing" : "checklist-report-grid"}
              style={{
                gridTemplateColumns: `${this.state.splitPct}% 12px minmax(320px, 1fr)`,
              }}
            >
              <section className="checklist-panel">
                <div className="checklist-panel-head">
                  <strong>Runs</strong>
                  <div>
                    <button type="button" disabled={this.state.isExporting} onClick={this.exportExcel}>
                      {this.state.isExporting ? "Exporting..." : "Export Excel"}
                    </button>
                    <span>{this.state.isLoading ? "Loading..." : `${rows.length} records · Page ${this.state.page}`}</span>
                  </div>
                </div>
                <div ref={this.tableWrapRef} className="checklist-table-wrap">
                  {this.state.isLoading ? (
                    <div className="checklist-detail-loading">
                      <GridLoader size={7} color={"#222222"} loading />
                      <span>Loading runs</span>
                    </div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Completed</th>
                          <th>Driver</th>
                          <th>Asset</th>
                          <th>Depot</th>
                          <th>Checklist</th>
                          <th>Status</th>
                          <th>Items</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>{this.renderRows()}</tbody>
                    </table>
                  )}
                </div>
                <div className="checklist-pagination">
                  <button
                    type="button"
                    disabled={this.state.isLoading || this.state.page <= 1}
                    onClick={() => this.loadRuns(this.state.page - 1)}
                  >
                    Previous 100
                  </button>
                  <span>Page {this.state.page}</span>
                  <button
                    type="button"
                    disabled={this.state.isLoading || !this.state.hasMore}
                    onClick={() => this.loadRuns(this.state.page + 1)}
                  >
                    Next 100
                  </button>
                </div>
              </section>

              <button
                type="button"
                className="checklist-splitter"
                onMouseDown={this.startResize}
                aria-label="Resize report panels"
                title="Drag to resize panels"
              />

              <section className="checklist-panel detail">
                <div className={`checklist-panel-head ${selected && String(getValue(selected, "status") || "").toLowerCase() === "fail" ? "fail" : ""}`}>
                  <strong>Run Details</strong>
                  <div>
                    <button type="button" disabled={!canExport} onClick={this.exportPdf}>Export PDF</button>
                    <span>{selected ? getValue(selected, "displayId") || "Selected" : "Select a run"}</span>
                  </div>
                </div>
                {this.renderDetail()}
              </section>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
}

export default ChecklistRunsReport;
